import { CSSProperties, useMemo } from 'react';
import {
  ComponentFactory,
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { isColorBright, resolveColorProperty } from '@gn-audio/jss-components/utils';

import { executeGQLQuery } from 'api/graphqlApi';
import { Eyebrow } from 'common/components/Eyebrow';
import { getSharedProperty, mapSharedPropertiesToFields } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import { classNames } from 'common/utils/classNames';
import { PlaceholderContainerContext } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import { ComponentProps } from 'lib/component-props';
import { isNotNil } from 'utils/utils';

type GenericKSPSpotLayout = 'vertical' | 'horizontal';

type PlaceholderDatasource = {
  name: string;
  id: string;
  displayName: string;
  fields: Record<string, Field>;
};
type GenericKSPSpotFields = {
  backgroundColor: Field<string>;
  backgroundColorShared: Field<string>;
  placeholder: Array<PlaceholderDatasource>;
  layoutOrientation: Field<GenericKSPSpotLayout>;
  layoutOrientationShared: Field<GenericKSPSpotLayout>;
  dataSource: {
    fields: GenericKSPSpot;
  };
  dataSourceShared: {
    fields: GenericKSPSpot;
  };
  text: Field<string>;
  header: Field<string>;
  title: Field<string>;
};

type PlaceholderTemplate = {
  item: {
    id: string;
    name: string;
    template: {
      name: string;
    };
  };
};
type GenericKSPSpotProps = {
  fields: GenericKSPSpotFields;
  placeholderTemplates: PlaceholderTemplate[];
  componentFactory: ComponentFactory;
} & ComponentProps;

type GenericKSPSpot = {
  Header: Field<string>;
  Title: Field<string>;
  Text: Field<string>;
};

const KSP = ({ fields, placeholderTemplates, componentFactory }: GenericKSPSpotProps) => {
  const kspDataSource = fields.dataSource ?? fields.dataSourceShared;
  const kspFields = kspDataSource?.fields;

  const variant = getSharedProperty(fields, 'layoutOrientation')?.value || 'vertical';
  const backgroundColorHex = resolveColorProperty(
    getSharedProperty(fields, 'backgroundColor')?.value.toLowerCase() ?? '#ffffff'
  );
  const backgroundTextColorClass = isColorBright(backgroundColorHex) ? 'light' : 'dark';

  const backgroundStyle = {
    backgroundColor: backgroundColorHex,
  } as CSSProperties;

  const paddingOverwrites = {
    '--top-padding-mobile': 0,
    '--top-padding-tablet': 0,
    '--top-padding-desktop': 0,
    '--bottom-padding-mobile': 0,
    '--bottom-padding-tablet': 0,
    '--bottom-padding-desktop': 0,
  } as CSSProperties;
  const header = getSharedDictionary(fields, 'header')?.value || kspFields?.Header.value || '';
  const title = getSharedDictionary(fields, 'title')?.value || kspFields?.Title.value || '';
  const text = getSharedDictionary(fields, 'text')?.value || kspFields?.Text.value || '';

  const components = placeholderTemplates
    .map((template, index) => {
      const dataSource = fields.placeholder[index];
      if (!template.item.template) {
        return;
      }
      const Component = componentFactory(template.item.template.name);
      if (!Component) {
        return;
      }
      return {
        Component,
        id: dataSource.id,
        props: {
          fields: dataSource.fields,
          rendering: { fields: dataSource.fields, dataSource: dataSource.id },
        },
      };
    })
    .filter(isNotNil);

  const context = useMemo(() => {
    return { isInsidePlaceholder: true };
  }, []);
  return (
    <div
      style={backgroundStyle}
      className={classNames(
        'container-fluid flex flex-col gap-12 md:gap-24 lg:gap-32',
        backgroundTextColorClass
      )}
    >
      {variant === 'horizontal' && (
        <div className="container mx-auto flex w-full max-w-6xl justify-center gap-6 py-0 lg:px-0">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <Eyebrow text={title} />
            </div>
            <div className="flex w-full flex-col gap-5 xs:flex-row xs:gap-10">
              <h2
                className="text-5xl font-bold leading-[52px]"
                dangerouslySetInnerHTML={{ __html: sanitize(header) }}
              />
              <div
                className="flex w-fit flex-col gap-[10px] text-2xl font-normal leading-8 lg:w-[536px] lg:pl-[60px]"
                dangerouslySetInnerHTML={{ __html: sanitize(text) }}
              />
            </div>
          </div>
        </div>
      )}
      {variant === 'vertical' && (
        <div className="container flex max-w-6xl justify-center gap-6 py-0 text-center">
          <div className="flex w-full flex-col gap-10 sm:items-center">
            <div>
              <Eyebrow text={title} align="center" />
            </div>
            <div className="flex w-full justify-center">
              <h2
                className="text-5xl font-bold leading-[52px]"
                dangerouslySetInnerHTML={{ __html: sanitize(header) }}
              />
            </div>
            <div
              className="flex max-w-[1087px] flex-col gap-[10px] text-2xl font-normal leading-8"
              dangerouslySetInnerHTML={{ __html: sanitize(text) }}
            ></div>
          </div>
        </div>
      )}
      {!!components.length && (
        <div
          className="placeholder-container flex flex-col gap-12 md:gap-24 lg:gap-32"
          style={paddingOverwrites}
        >
          <PlaceholderContainerContext.Provider value={context}>
            {components.map((component) => {
              return <component.Component key={component?.id} {...component.props} />;
            })}
          </PlaceholderContainerContext.Provider>
        </div>
      )}
    </div>
  );
};

const query = `query GetItemTemplate($path: String!, $language: String!) {
  item(path: $path, language: $language) {
    id
    name
    template {
      name
    }
  }
}`;

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  const mappedFields = mapSharedPropertiesToFields<GenericKSPSpotFields>(
    rendering.fields as SitecoreFieldTypes
  );

  const placeholder = mappedFields.placeholder;
  const placeholderIds = placeholder.map((item) => {
    return item.id;
  });

  const placeholderTemplates = await Promise.all(
    placeholderIds.map((id) => {
      return executeGQLQuery(query, {
        path: id,
        language: 'en',
      });
    })
  );

  return { fields: rendering.fields, placeholderTemplates };
};

export default withDatasourceCheck()(KSP);
